/* eslint-disable ssr-friendly/no-dom-globals-in-react-fc */
import * as React from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import throttle from 'lodash/throttle';
import { ThemeProvider } from 'styled-components';
import { globalFilterTokens } from '../../../tokens/components/GlobalFilter/tokens';
import { ToolbarStack, ToolbarWrapperContainer, MoreWrapper } from '../styles';
import { MoreMeasurer, Measurement } from './MoreMeasurer';

export type StateType =
  | {
      wraps: true;
      from: number;
      count: number;
    }
  | {
      wraps: false;
      from: null;
      count: null;
    };

export interface ToolbarWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  styleProps?: {
    gap?: string;
    marginY?: string;
    height?: string;
  };
  children: (props: StateType) => React.ReactNode | React.ReactNode[];
  more: (props: StateType) => React.ReactNode;
}

const split = <T extends any>(arr: T[], predicate: (e: T) => boolean): [T[], T[]] => {
  const truthy: T[] = [];
  const falsy: T[] = [];

  arr.forEach(e => (predicate(e) ? truthy : falsy).push(e));

  return [truthy, falsy];
};

export const ToolbarWrapper: React.FC<ToolbarWrapperProps> = ({ children, more, styleProps, ...props }) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [state, setState] = React.useState<StateType>({
    wraps: false,
    from: null,
    count: null,
  });
  const [{ width: moreWidth, gap: toolbarGap }, setMoreWrapperWidth] = React.useState<Measurement>({
    width: 0,
    gap: 0,
  });

  // prettier-ignore
  const {
    gap = globalFilterTokens.ToolbarGapBaseDefault,
    marginY = '6px',
    height = '30px',
  } = styleProps || {};

  React.useEffect(() => {
    if (!wrapperRef.current) {
      return;
    }
    const observer = new ResizeObserver(
      throttle((entries: Parameters<ResizeObserverCallback>[0]) => {
        // we only care about the most recent event
        const event = entries.slice(-1).pop();

        if (!event) {
          return; // shouldn't be possible, but we want to make ts happy
        }

        /** <ToolbarWrapperContainer> */
        const container = event.target as HTMLElement;

        /** the wrapper surrounding {children} */
        const parent = container.firstChild as HTMLElement;

        /* Reset the state to the default so that the toolbar renders fully and we're able to measure its children */
        setState({
          wraps: false,
          from: null,
          count: null,
        });

        /** children: [tag] [tag] [tag] [more]  */
        const children = Array.from(parent.children) as HTMLElement[];
        /** array of children of the wrapper, excluding the last one because that's always props.more */
        const tags = children.slice(0, -1);

        if (!tags.length) {
          // https://www.youtube.com/watch?v=fqGKZ3fzN1M
          return;
        }

        // Children with different offsetTops from first, are wrapping
        const firstTagTop = tags[0].offsetTop;
        const [wrappingTags, nonWrappingTags] = split(tags, e => e.offsetTop !== firstTagTop);

        const parentContainerRight = container.getBoundingClientRect().right;

        if (wrappingTags.length === 0) {
          // We still need to calculate that there's enough space to fit
          // the last Tag's content + Reset button (encapsulated in MoreWrapper)
          const lastTag = nonWrappingTags[nonWrappingTags.length - 1].getBoundingClientRect();

          // Width needed for last Tag + Reset button
          // .getBoundingClientRect() does not account for borders/margins
          const spaceNeeded = lastTag.width + moreWidth + toolbarGap;

          // Distance between the start of current Tag (left) and the
          // end of parent container (right)
          const spaceAvailable = Math.round(parentContainerRight - lastTag.left);

          if (spaceAvailable > spaceNeeded) {
            return;
          }
        }

        let firstChildThatFits = -1;

        for (let i = nonWrappingTags.length - 1; i >= 0; i--) {
          const currentTag = nonWrappingTags[i].getBoundingClientRect();

          // Width needed for current Tag + MoreWrapper
          // .getBoundingClientRect() does not account for borders/margins
          const spaceNeeded = currentTag.width + moreWidth + toolbarGap;

          // Distance between the start of current Tag (left) and the
          // end of parent container (right)
          const spaceAvailable = Math.round(parentContainerRight - currentTag.left);

          if (spaceAvailable > spaceNeeded) {
            firstChildThatFits = i;
            break;
          }
        }

        const from = firstChildThatFits + 1;

        setState({
          wraps: true,
          from,
          count: tags.length - from,
        });
      }, 100),
    );

    observer.observe(wrapperRef.current);

    return () => {
      observer.disconnect();
    };
  }, [wrapperRef, moreWidth, toolbarGap]);

  const rendered = children(state);

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        toolbarHorizontalSpacing: gap,
        toolbarVerticalSpacing: marginY,
        toolbarHeight: height,
      })}
    >
      <ToolbarWrapperContainer ref={wrapperRef}>
        <ToolbarStack role="group" {...props}>
          {state.wraps ? flattenChildren(rendered).slice(0, state.from) : rendered}
          <MoreWrapper>{more(state)}</MoreWrapper>
        </ToolbarStack>
      </ToolbarWrapperContainer>

      <MoreMeasurer more={more} onMount={measurement => setMoreWrapperWidth(measurement)} />
    </ThemeProvider>
  );
};

ToolbarWrapper.displayName = 'GlobalFilter.ToolbarWrapper';
